import { memo, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import DeleteTrashIcon from 'components/Icons/DeleteTrashIcon';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import {
  PLAN_DIM_MODAL_CLEAR_COLOR,
  PLAN_DIM_MODAL_RADIUS,
  PLAN_DIM_MODAL_SIDEBAR_ACTIVE_BG,
  PLAN_DIM_MODAL_SIDEBAR_BG,
  PLAN_DIM_MODAL_SIDEBAR_WIDTH,
  PLAN_DIM_MODAL_WIDTH,
} from '../planDimSelectionModalStyles';
import { PLAN_REVAMP_RADIUS_SM, PLAN_TEXT } from '../planPageRevampUi';
import PlanFilterSearchIcon from '../icons/PlanFilterSearchIcon';

import type { PlanDimDraftSelections } from '../planDimSelectionUtils';

export interface PlanDimensionOption {
  id: number;
  name: string;
  items: string[];
}

interface PlanDimSelectionModalProperties {
  isOpen: boolean;
  dimensions: PlanDimensionOption[];
  activeDimensionName: string;
  draftSelections: PlanDimDraftSelections;
  searchQuery: string;
  onClose: () => void;
  onApply: () => void;
  onClearAll: () => void;
  onActiveDimensionChange: (dimensionName: string) => void;
  onSearchChange: (value: string) => void;
  onToggleItem: (dimensionName: string, itemName: string, checked: boolean) => void;
}

const PlanDimSelectionModal = ({
  isOpen,
  dimensions,
  activeDimensionName,
  draftSelections,
  searchQuery,
  onClose,
  onApply,
  onClearAll,
  onActiveDimensionChange,
  onSearchChange,
  onToggleItem,
}: PlanDimSelectionModalProperties) => {
  const themeColors = useSelector(getPlanPageThemeColors);

  const visibleDimensions = useMemo(
    () => dimensions.filter((dimension) => (dimension.items?.length ?? 0) > 0),
    [dimensions],
  );

  const activeDimension = useMemo(
    () =>
      visibleDimensions.find((dimension) => dimension.name === activeDimensionName) ??
      visibleDimensions[0],
    [activeDimensionName, visibleDimensions],
  );

  const filteredItems = useMemo(() => {
    const items = activeDimension?.items ?? [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => item.toLowerCase().includes(query));
  }, [activeDimension?.items, searchQuery]);

  const selectedItems = activeDimension ? draftSelections[activeDimension.name] ?? [] : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size='xl'>
      <ModalOverlay bg='blackAlpha.400' />
      <ModalContent
        maxW={PLAN_DIM_MODAL_WIDTH}
        borderRadius={PLAN_DIM_MODAL_RADIUS}
        overflow='hidden'
        mx={4}
      >
        <ModalHeader
          px={6}
          py={4}
          borderBottom='1px solid'
          borderColor={themeColors.border}
          bg={themeColors.surface}
        >
          <Flex alignItems='center' justifyContent='space-between' gap={4}>
            <Text {...PLAN_TEXT.modalTitle} color={themeColors.textDark}>
              Filters
            </Text>
            <InputGroup maxW='220px' flexShrink={0}>
              <InputLeftElement pointerEvents='none' h='36px'>
                <PlanFilterSearchIcon width={16} height={16} color={themeColors.primary} />
              </InputLeftElement>
              <Input
                h='36px'
                pl={9}
                borderRadius='full'
                borderColor={themeColors.border}
                placeholder='Search'
                value={searchQuery}
                onChange={(event) => onSearchChange(event.target.value)}
                {...PLAN_TEXT.labelSm}
                _placeholder={{ color: themeColors.textSecondary }}
                _focusVisible={{ borderColor: themeColors.primary, boxShadow: 'none' }}
              />
            </InputGroup>
          </Flex>
        </ModalHeader>

        <ModalBody p={0} bg={themeColors.surface}>
          <Flex minH='320px' maxH='420px'>
            <Box
              w={PLAN_DIM_MODAL_SIDEBAR_WIDTH}
              flexShrink={0}
              bg={PLAN_DIM_MODAL_SIDEBAR_BG}
              borderRight='1px solid'
              borderColor={themeColors.border}
              overflowY='auto'
              className='scroll'
            >
              {visibleDimensions.map((dimension) => {
                const isActive = dimension.name === activeDimension?.name;
                return (
                  <Box
                    key={dimension.id}
                    px={4}
                    py={3}
                    cursor='pointer'
                    bg={isActive ? PLAN_DIM_MODAL_SIDEBAR_ACTIVE_BG : 'transparent'}
                    borderBottom='1px solid'
                    borderColor={themeColors.border}
                    onClick={() => onActiveDimensionChange(dimension.name)}
                    _hover={{ bg: PLAN_DIM_MODAL_SIDEBAR_ACTIVE_BG }}
                  >
                    <Text
                      {...(isActive ? PLAN_TEXT.bodySemibold : PLAN_TEXT.body)}
                      color={isActive ? themeColors.primary : themeColors.textDark}
                      noOfLines={1}
                    >
                      {dimension.name}
                    </Text>
                  </Box>
                );
              })}
            </Box>

            <Box flex='1' minW={0} overflowY='auto' className='scroll' px={4} py={3}>
              {filteredItems.length > 0 ? (
                filteredItems.map((itemName) => {
                  const isChecked = selectedItems.includes(itemName);
                  return (
                    <Flex
                      key={itemName}
                      alignItems='center'
                      justifyContent='space-between'
                      py={2.5}
                      borderBottom='1px solid'
                      borderColor={themeColors.border}
                      gap={3}
                    >
                      <Text {...PLAN_TEXT.body} color={themeColors.textDark} noOfLines={1} flex='1'>
                        {itemName}
                      </Text>
                      <Checkbox
                        isChecked={isChecked}
                        colorScheme='purple'
                        size='lg'
                        borderRadius='md'
                        onChange={(event) =>
                          onToggleItem(activeDimension!.name, itemName, event.target.checked)
                        }
                      />
                    </Flex>
                  );
                })
              ) : (
                <Flex h='100%' minH='200px' alignItems='center' justifyContent='center'>
                  <Text {...PLAN_TEXT.body} color={themeColors.textSecondary}>
                    No items found
                  </Text>
                </Flex>
              )}
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter
          flexDirection='column'
          gap={3}
          px={6}
          py={4}
          borderTop='1px solid'
          borderColor={themeColors.border}
          bg={themeColors.surface}
        >
          <Flex w='100%' gap={3}>
            <Button
              flex='1'
              h='44px'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              variant='outline'
              borderColor={themeColors.border}
              bg={themeColors.surface}
              {...PLAN_TEXT.button}
              fontSize='14px'
              color={themeColors.textDark}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              flex='1'
              h='44px'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              bg={themeColors.primary}
              color={themeColors.white}
              {...PLAN_TEXT.button}
              fontSize='14px'
              _hover={{ bg: themeColors.primaryHover }}
              _active={{ bg: themeColors.primaryPressed }}
              onClick={onApply}
            >
              Apply Filters
            </Button>
          </Flex>

          {visibleDimensions.length > 1 && (
            <Button
              variant='ghost'
              h='auto'
              minH='unset'
              p={0}
              display='inline-flex'
              alignItems='center'
              gap={2}
              color={PLAN_DIM_MODAL_CLEAR_COLOR}
              {...PLAN_TEXT.labelSmMedium}
              _hover={{ bg: 'transparent', opacity: 0.85 }}
              onClick={onClearAll}
            >
              <DeleteTrashIcon width={14} height={14} />
              Clear all Filters
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default memo(PlanDimSelectionModal);
